import React, { useEffect, useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle, Tag } from "lucide-react";
import api from "../../lib/api";

const Row = ({ label, value, ok }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-600">{label}</span>
    <span className={`text-sm font-semibold ${ok === false ? "text-amber-700" : "text-navy"}`}>{value}</span>
  </div>
);

export default function TrackingSetupPanel() {
  const [data, setData] = useState(null);
  const [ga4, setGa4] = useState(null);

  useEffect(() => {
    Promise.all([
      api.get("/admin/analytics/manifest").then((r) => r.data).catch(() => null),
      api.get("/admin/ga4/status").then((r) => r.data).catch(() => null),
    ]).then(([m, g]) => { setData(m); setGa4(g); });
  }, []);

  if (!data) {
    return <div className="flex items-center gap-2 text-slate-500" data-testid="tracking-loading">
      <Loader2 className="animate-spin" size={16} /> Loading…
    </div>;
  }

  const mp = ga4?.measurement_protocol || {};

  return (
    <div className="space-y-6" data-testid="tracking-setup-panel">
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <h3 className="font-bold text-navy flex items-center gap-2"><Tag size={18} className="text-orange" /> What's connected</h3>
        <div className="mt-3">
          <Row label="Google Tag Manager container" value={data.gtm_container || "Not set"} ok={Boolean(data.gtm_container)} />
          <Row label="GA4 measurement ID" value={data.ga4_measurement_id || "Not set"} ok={Boolean(data.ga4_measurement_id)} />
          <Row label="GA4 reporting into this admin area" value={ga4?.connected ? "Connected" : "Not connected"} ok={Boolean(ga4?.connected)} />
          <Row label="Confirmed bookings sent from the server" value={mp.configured ? "On" : "Not set up"} ok={Boolean(mp.configured)} />
        </div>
        <p className="text-xs text-slate-500 mt-3">{data.container_note}</p>
        {!mp.configured && (mp.needs || []).length > 0 && (
          <div className="mt-3 rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600" data-testid="tracking-mp-needs">
            <strong>To report bookings from the server</strong> — so a call still counts when the visitor closes the tab:
            <ul className="list-disc ml-5 mt-1">{mp.needs.map((n) => <li key={n}>{n}</li>)}</ul>
          </div>
        )}
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="tracking-events">
        <h3 className="font-bold text-navy">Events the site sends</h3>
        <p className="text-xs text-slate-500 mt-1">
          Conversions are marked. Every one fires on a completion that was verified, never on a click that
          merely intended it.
        </p>
        <div className="overflow-x-auto mt-4">
          <table className="min-w-full text-sm">
            <thead className="text-slate-600">
              <tr>
                <th className="text-left py-1 pr-4">Event</th>
                <th className="text-left py-1 pr-4">Counts as</th>
                <th className="text-left py-1 pr-4">When it fires</th>
                <th className="text-left py-1 pr-4">How we know it's genuine</th>
                <th className="text-left py-1">Old name still sent</th>
              </tr>
            </thead>
            <tbody>
              {data.events.map((e) => (
                <tr key={e.name} className="border-t border-slate-100 align-top">
                  <td className="py-1.5 pr-4 font-mono text-xs text-navy whitespace-nowrap">{e.name}</td>
                  <td className="py-1.5 pr-4">
                    {e.kind === "key"
                      ? <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-xs"><CheckCircle2 size={13} /> Conversion</span>
                      : <span className="text-slate-500 text-xs">Engagement</span>}
                  </td>
                  <td className="py-1.5 pr-4 text-slate-600">{e.fires}</td>
                  <td className="py-1.5 pr-4 text-slate-600">{e.verified}</td>
                  <td className="py-1.5 text-slate-500 text-xs">{e.legacy || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="tracking-gtm-steps">
        <h3 className="font-bold text-navy">Still to do inside Tag Manager</h3>
        <p className="text-xs text-slate-500 mt-1">
          The container is installed and the site is pushing events. These steps happen in the GTM account
          itself — nothing reaches GA4 until the container is published.
        </p>
        <ol className="list-decimal ml-5 mt-3 space-y-1.5 text-sm text-slate-600">
          {data.gtm_setup.map((s) => <li key={s}>{s}</li>)}
        </ol>
      </section>

      <section className="rounded-2xl border border-amber-200 bg-amber-50/50 p-5" data-testid="tracking-limitations">
        <h3 className="font-bold text-navy flex items-center gap-2"><AlertTriangle size={18} className="text-amber-600" /> What these numbers can't tell you</h3>
        <ul className="list-disc ml-5 mt-3 space-y-1.5 text-sm text-slate-700">
          {data.limitations.map((l) => <li key={l}>{l}</li>)}
        </ul>
      </section>
    </div>
  );
}
